import React, { useContext } from 'react';
import * as PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import { Typography } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';

import { t } from '../../../../../../utils/i18next';
import CurrencyFormat from '../../../../general/CurrencyFormat';
import { spacingInRems } from '../../../../../theme/themeUtils';
import { StyledTooltip } from '../../../../../theme/styledElements';
import { storeContext } from '../../../../../context';
import PaymentItem from './PaymentItem';
import DiscountHeader from '../../../../general/DiscountHeader';
import styled from 'styled-components';
import { dateToOrdinalDay } from '../../../../../../utils/Date';
import { numberToCurrency } from '../../../../../../utils/String';

const translationBase = 'payables.total_side_bar.payment';

/**
 * A components which shows title, amount and an optional selected bills amount
 * @param {HTMLElement} children - expected button element
 * @param {String} amount - amount to be displayed as currency format
 * @param {String} type - one of PaymentItem.paymentTypes
 */

const StyledTotalDiscount = styled(Typography)`
  display: flex;
  align-items: center;
  margin-top: ${spacingInRems(0.5)};
  svg {
    width: 2rem;
    height: 2rem;
  }
`;

const StyledDiscountContainer = styled.div`
  width: 100%;
`;

const PaymentItemData = observer(({ children, amount, type }) => {
  const {
    payable,
    translations: { currentLanguage },
    settings
  } = useContext(storeContext);

  const { VIEW_PAYMENT_PLAN_OPTIONS } = PaymentItem.paymentTypes;

  const getTitle = () => {
    if (type === VIEW_PAYMENT_PLAN_OPTIONS) {
      return 'payables.total_side_bar.assist.payment_plan';
    } else if (settings.get('display_SB490_verbiage_in_PE')) {
      return `${translationBase}.estimated_amount_due`;
    } else {
      return `${translationBase}.amount_due`;
    }
  };

  const getSelectedTotal = () => {
    if (payable.hasBillSelectable) {
      return (
        <Typography
          variant="subtitle1"
          className={'selectBillTotal'}
          color={'textPrimary'}
          data-testid="select-a-bill-amount-total"
        >
          <span>{t(`payables.payment_options.message.select_a_bill`)}: </span>
          <CurrencyFormat value={payable.selectableNextPayment} />
        </Typography>
      );
    }
  };

  const getTotalDiscountApplied = () => {
    if (payable.totalBillsDiscount) {
      return (
        <StyledTotalDiscount
          variant={'body2'}
          className={'totalBillsDiscount'}
          color={'textPrimary'}
          data-testid="total-bills-discount"
        >
          <span>
            {t(`${translationBase}.discount.discounts_applied`)}&nbsp;
          </span>
          <CurrencyFormat value={payable.totalBillsDiscount} />
          <StyledTooltip
            placement={'top-end'}
            arrow={true}
            title={t(`${translationBase}.discount.tooltip`)}
          >
            <InfoIcon color={'secondary'} />
          </StyledTooltip>
        </StyledTotalDiscount>
      );
    }
  };

  const getEligibleDiscount = () => {
    if (payable.hasPromptPayDiscount) {
      return (
        <StyledDiscountContainer>
          <DiscountHeader
            discountData={payable.discount}
            includeDate={true}
            textVariant={'subtitle2'}
            fullWidth={true}
          />
        </StyledDiscountContainer>
      );
    }
  };

  const getEstimatedInfo = () => {
    if (payable.hasAppointments && payable.appointmentsEstimatedAmountDue > 0) {
      return (
        <Typography variant={'body1'}>
          <InfoIcon fontSize={'small'} sx={{ verticalAlign: 'bottom' }} />
          {t(`${translationBase}.estimated_note`, {
            amount: numberToCurrency(payable.appointmentsEstimatedAmountDue)
          })}
        </Typography>
      );
    }
  };

  return (
    <>
      <Typography variant="subtitle1" color={'textPrimary'}>
        {t(getTitle())}
      </Typography>
      <Typography className="amount">
        <CurrencyFormat value={Math.max(amount, 0)} />
      </Typography>
      {getEstimatedInfo()}
      {getEligibleDiscount()}
      {getTotalDiscountApplied()}
      <div className="buttonContainer">{children}</div>
      {getSelectedTotal()}
    </>
  );
});

PaymentItemData.propTypes = {
  type: PropTypes.number,
  amount: PropTypes.number,
  children: PropTypes.node.isRequired
};

export default PaymentItemData;
